import { useThree } from '@react-three/fiber';
import React, { ReactElement, FunctionComponent, memo, Suspense, useEffect, useState } from 'react';
import { ISceneActions, useDataStore, useSceneStore } from '../../../zustand';
import { IContent360Scene, ITrackingTypes } from '@r3f-component-data-structure';
import { PerspectiveCamera, Texture } from 'three';
import { useGetCurrentSceneId } from '../../../hooks/general';
import Background360 from '@r3f/components/Background360';
import { Jute } from '../../..';
import { useDeviceHasGyroscope } from '../deviceOrientationHooks';
import { useGetActiveTrackingType } from '../../hoc/hocCustomHooks';
import { useCallFirstSceneIs360CameraFrameCallback, useIsDeviceOrientationEnabled, useSetup360SceneBehaviour, useSetupFirstSceneIs360Behaviour, useProjectHas360Scenes } from './content360Hooks';
import DragControlsWrapper from '../../DragControlsWrapper';
import { DeviceOrientationControlsWrapper } from './DeviceOrientationControls/DeviceOrientationControlsWrapper';
import LoadingSphere from '@r3f/components/LoadingSphere';
import { filestore, FsImage360, FsVideo360 } from '@/filestore';

type Background360Media = { imageTexture?: Texture, videoUrl?: string }

type IParentProps = {
	enabled: boolean;
	children: ReactElement[] | undefined;
	onShowContentFirstTime?: () => any;
};

//@ts-ignore
const content360Camera = new PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000, []);
content360Camera.name = 'content360Camera';

export const Content360Wrapper: FunctionComponent<IParentProps> = ({ enabled, children, onShowContentFirstTime }) => {
    const { scene } = useThree();
    const componentsById = useDataStore(({ componentsById }) => componentsById);
	const currentSceneId = useGetCurrentSceneId();
    const currentScene = componentsById?.[currentSceneId] as IContent360Scene;
    const [isJuteLaunched, setIsJuteLaunched] = useState(false);
    const [media, setMedia] = useState<Background360Media>();
    const sceneActionDispatch = useSceneStore(({dispatch}) => dispatch)

    useEffect(() => {
        if (Jute.isLaunched) setIsJuteLaunched(true)
        else Jute.onLaunch(() => setIsJuteLaunched(true))
    }, [])
    
    useSetup360SceneBehaviour();
    //@ts-ignore
    useSetupFirstSceneIs360Behaviour(onShowContentFirstTime);
    useCallFirstSceneIs360CameraFrameCallback();
    const projectHas360Scenes = useProjectHas360Scenes()
    const deviceHasGyroscope = useDeviceHasGyroscope(projectHas360Scenes, Jute.usesOrbitCamera);
    const isDeviceOrientationEnabled = useIsDeviceOrientationEnabled(deviceHasGyroscope, Jute.usesOrbitCamera);
    const activeTrackingType = useGetActiveTrackingType();
    
    // Where `useSwitchDefaultCameras` hands the default camera back when a 360 scene is left. A world
    // scene in the preview is rendered by drei's `preview-orbit-camera`, and this hook's layout effect
    // runs AFTER drei's — so naming the Zappar camera here silently wins, and the scene comes up on a
    // camera driven by `rawPose` that OrbitControls does not touch.
    const leavingTo = Jute.isPreviewSimulator && activeTrackingType === ITrackingTypes.world
        ? 'preview-orbit-camera'
        : Jute.usesOrbitCamera ? 'non-ar-camera' : 'zappar-camera';
    const nonDragCamera = scene.getObjectByName(leavingTo) as PerspectiveCamera;
    const isMobile = !Jute.isPico && !Jute.isQuest && !Jute.isZapbox;

    const onVideoNodeReady = () => {
        sceneActionDispatch({
            type: ISceneActions.SET_VIDEO_TEXTURE_AVAILABLE,
            payload: { sceneId: currentSceneId, available: true }
        })
    }

    useEffect(() => {
        if (typeof currentScene.content360?.filestoreId === 'undefined') return

        if (currentScene.content360?.type === 'image') {
            const imgFile = filestore.get<FsImage360>(currentScene.content360.filestoreId)
            imgFile.getTexture().then(txt => { setMedia({ imageTexture: txt, videoUrl: undefined }) })
        } else {
            setMedia({
                imageTexture: undefined,
                videoUrl: filestore.get<FsVideo360>(currentScene.content360.filestoreId).getPlaylistUrl()
            })
        }
    }, [currentScene.content360?.filestoreId])

	return (
        <>
            <DragControlsWrapper enabled={enabled} camera={content360Camera} nonDragCamera={nonDragCamera} disableVerticalDrag={deviceHasGyroscope} />
            {isDeviceOrientationEnabled && deviceHasGyroscope && !Jute.usesOrbitCamera && <DeviceOrientationControlsWrapper camera={content360Camera} enabled={enabled && isMobile && isJuteLaunched} /> }
            {children}
            {currentScene.content360?.filestoreId && media ? 
                <Suspense fallback={<LoadingSphere radius={60} renderOrder={-99999999999}/>}>
                    <Background360
                        media={media}
                        type={currentScene.content360.type}
                        radius={60}
                        onVideoNodeReady={onVideoNodeReady}
                        environmentRenderPriority={11}
                        sceneId={currentSceneId}
                        loop={currentScene.content360.type === 'video' ? currentScene.content360.loop : undefined}
                        muted={currentScene.content360.type === 'video' ? currentScene.content360.muted : undefined}
                        addVideoToDom={Jute.isAppClip}
                    />
                </Suspense>
                : null
            }
        </>
	);
};

Content360Wrapper.displayName = 'Content360Wrapper';
export default memo(Content360Wrapper);
